"use client"

import { useState, useEffect, useCallback } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { Fragment } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { useSavedCards } from "@/hooks/useSavedCards"
import {
    VisaIcon,
    MastercardIcon,
    AmexIcon,
    DiscoverIcon,
    DefaultCardIcon,
} from "@/components/presentational/tailwind/CardBrandIcons"

interface SkipTrialDialogProps {
    open: boolean
    onClose: () => void
    accountId: string
    planTier: "go" | "grow" | "glory"
    firstMonthPrice: number // cents (or yearly price for yearly plans)
    ccTrialEndsAt: string // ISO timestamp
    userId: string
    onSuccess: () => void
    isYearly?: boolean
}

const TIER_LABELS: Record<string, string> = {
    go: "Go",
    grow: "Grow",
    glory: "Glory",
}

const brandIconMap: Record<string, React.FC<{ className?: string }>> = {
    visa: VisaIcon,
    mastercard: MastercardIcon,
    amex: AmexIcon,
    american_express: AmexIcon,
    americanexpress: AmexIcon,
    discover: DiscoverIcon,
}

function getBrandIcon(brand: string | null | undefined): React.FC<{ className?: string }> {
    if (!brand) return DefaultCardIcon
    const lower = brand.toLowerCase().replace(/[\s\-]+/g, "_")
    return brandIconMap[lower] || DefaultCardIcon
}

function formatBrandName(brand: string | null | undefined): string {
    if (!brand) return "Card"
    const lower = brand.toLowerCase().replace(/[\s\-]+/g, "_")
    if (lower === "visa") return "Visa"
    if (lower === "mastercard") return "Mastercard"
    if (lower === "amex" || lower === "american_express" || lower === "americanexpress") return "AMEX"
    if (lower === "discover") return "Discover"
    return "Card"
}

function formatPrice(cents: number): string {
    const dollars = cents / 100
    return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

type DialogState = "confirm" | "loading" | "success" | "error"

export default function SkipTrialDialog({
    open,
    onClose,
    accountId,
    planTier,
    firstMonthPrice,
    ccTrialEndsAt,
    userId,
    onSuccess,
    isYearly,
}: SkipTrialDialogProps) {
    const [state, setState] = useState<DialogState>("confirm")
    const [errorMessage, setErrorMessage] = useState("")
    const { savedCards, isLoading: cardsLoading, selectedCard } = useSavedCards(userId)

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setState("confirm")
            setErrorMessage("")
        }
    }, [open])

    const handleSkipTrial = useCallback(async () => {
        if (state === "loading") return
        setState("loading")
        setErrorMessage("")

        try {
            const res = await fetch("/api/chargebee/skip-trial", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ accountId }),
            })

            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || "Payment failed. Please try again.")
            }

            setState("success")
            setTimeout(() => {
                onSuccess()
            }, 1500)
        } catch (err: any) {
            setState("error")
            setErrorMessage(err.message || "Something went wrong. Please try again.")
        }
    }, [accountId, state, onSuccess])

    const tierLabel = TIER_LABELS[planTier] || "Go"
    const priceDisplay = formatPrice(firstMonthPrice)
    const card = selectedCard || savedCards[0] || null
    const BrandIcon = card ? getBrandIcon(card.brand) : DefaultCardIcon
    const brandName = card ? formatBrandName(card.brand) : "Card"

    return (
        <Transition appear show={open}>
            <Dialog className="relative z-10" as="div" style={{ zIndex: 9999999 }} onClose={state === "loading" || state === "success" ? () => {} : onClose}>
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                {/* Modal container */}
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95 translate-y-4"
                            enterTo="opacity-100 scale-100 translate-y-0"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100 translate-y-0"
                            leaveTo="opacity-0 scale-95 translate-y-4"
                        >
                            <Dialog.Panel className="relative w-full max-w-sm overflow-hidden rounded-[20px] bg-ink p-6">
                                {/* Close button */}
                                <button
                                    onClick={onClose}
                                    className="absolute right-1.5 top-1.5 rounded-full p-3 text-white/40 transition-colors hover:bg-white/10 hover:text-white/80"
                                    aria-label="Close"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>

                                {/* Success state */}
                                {state === "success" ? (
                                    <div className="flex flex-col items-center py-4 text-center">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20">
                                            <svg
                                                className="h-6 w-6 text-emerald-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth={2.5}
                                                stroke="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        </div>
                                        <h3 className="mt-4 text-[20px] font-semibold text-white">Plan started!</h3>
                                        <p className="mt-1.5 text-[14px] text-white/60">
                                            Your {tierLabel} plan is now active
                                        </p>
                                    </div>
                                ) : (
                                    /* Confirm / Loading / Error states */
                                    <div className="flex flex-col">
                                        <h3 className="pr-8 text-[20px] font-semibold leading-tight text-white">
                                            Start your {tierLabel} plan now
                                        </h3>
                                        <p className="mt-2 text-[14px] leading-relaxed text-white/60">
                                            Skip the trial and pay {priceDisplay} for your first {isYearly ? "year" : "month"} today.
                                        </p>

                                        {/* Card on file */}
                                        {cardsLoading ? (
                                            <div className="mt-5 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
                                                <div className="h-5 w-8 animate-pulse rounded bg-white/10" />
                                                <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
                                            </div>
                                        ) : card ? (
                                            <div className="mt-5 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
                                                <BrandIcon className="h-5" />
                                                <span className="text-[14px] text-white/80">
                                                    {brandName} ending in {card.lastFour ?? "····"}
                                                </span>
                                            </div>
                                        ) : null}

                                        {/* Use different card link */}
                                        {!cardsLoading && card && (
                                            <a
                                                href="/update-payment"
                                                className="mt-2 self-start py-2 text-[13px] text-white/40 underline decoration-white/20 underline-offset-2 transition-colors hover:text-white/60"
                                            >
                                                Use a different card
                                            </a>
                                        )}

                                        {/* Error message */}
                                        {state === "error" && errorMessage && (
                                            <p className="mt-4 text-[13px] text-rose-400">{errorMessage}</p>
                                        )}

                                        {/* CTA button */}
                                        <button
                                            onClick={handleSkipTrial}
                                            disabled={state === "loading" || cardsLoading || (!card && !cardsLoading)}
                                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[rgb(255,163,209)] px-6 py-3.5 text-[15px] font-bold text-ink transition-colors hover:bg-[#fc83bf] disabled:opacity-50 disabled:hover:bg-[rgb(255,163,209)]"
                                        >
                                            {state === "loading" ? (
                                                <>
                                                    <svg
                                                        className="h-4 w-4 animate-spin"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        aria-hidden="true"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        />
                                                    </svg>
                                                    Processing...
                                                </>
                                            ) : state === "error" ? (
                                                "Try again"
                                            ) : (
                                                `Pay ${priceDisplay} now & start plan`
                                            )}
                                        </button>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
